import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import {
  ActiveErrorData,
  DevicesStore,
  DeviceTypes,
  DIAGNOSTICS_CONNECTION_STATUS,
  GatewayStore,
  InstallationDetailsStore,
  InstallationFeaturesStore,
  RemoteDiagnosticsStore,
} from '@store';

import { PillType } from '../../../../../shared/components/pill/pill.model';
import { DatesService } from '../../../../../shared/services/dates.service';
import { InstallationsService } from '../../../../services/installations.service';
import { matTooltipSetter } from '../../../../../shared/functions/mat-tooltip-setter/mat-tooltip-setter';
import {
  DeviceNameCommands,
  DeviceNameProperties,
  Feature,
  FeatureNames,
  FeaturePropertyKey,
} from '@vi-iot/client/index';
import { TranslateService } from '@ngx-translate/core';
import { HTMLExtractionPrefix } from 'src/app/shared/common/enums';

@Component({
  selector: 'app-active-error',
  templateUrl: './active-error.component.html',
  styleUrls: ['./active-error.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActiveErrorComponent implements OnInit, OnDestroy {
  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }

  connectionStatus: DIAGNOSTICS_CONNECTION_STATUS;
  mainLogicalLinkFromTopology: string;
  installationLocalTimeOfErrorAppearedAt: string;
  HTMLExtractionPrefix = HTMLExtractionPrefix;
  readonly deviceTypes = DeviceTypes;
  readonly pillType = PillType;
  readonly diagnosticsConnectionStatus = DIAGNOSTICS_CONNECTION_STATUS;
  readonly featurePropertyKey = FeaturePropertyKey;
  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  @Input() activeError: ActiveErrorData;
  @Input() remoteDiagnosticsActiveSessionDeviceSerial: string;
  @Output() navigate = new EventEmitter<{
    gatewaySerial: string;
    deviceSerial: string;
    activeErrorId: number;
    isLockedSystem?: boolean;
  }>();

  get moreInfoDisabled(): boolean {
    return (
      this.connectionStatus === this.diagnosticsConnectionStatus.LOADING ||
      this.mainLogicalLinkFromTopology === '' ||
      (this.remoteDiagnosticsActiveSessionDeviceSerial !== undefined &&
        this.remoteDiagnosticsActiveSessionDeviceSerial !== this.activeError.deviceSerial)
    );
  }

  constructor(
    protected gatewayStore: GatewayStore,
    public installationDetailsStore: InstallationDetailsStore,
    public installationsService: InstallationsService,
    public devicesStore: DevicesStore,
    public installationFeaturesStore: InstallationFeaturesStore,
    private datesService: DatesService,
    private remoteDiagnosticsStore: RemoteDiagnosticsStore,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscribeToTimezone();

    this.remoteDiagnosticsStore
      .getConnectionStatus()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((status) => {
        this.connectionStatus = status;
        this.cdr.detectChanges();
      });
    this.remoteDiagnosticsStore
      .getMainLogicalLinkFromTopology(this.activeError.gatewaySerial)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((logicalLink) => {
        this.mainLogicalLinkFromTopology = logicalLink;
      });
  }

  navigateToErrorDetails(): void {
    this.navigate.emit({
      gatewaySerial: this.activeError.gatewaySerial,
      deviceSerial: this.activeError.deviceSerial,
      activeErrorId: this.activeError.id,
      isLockedSystem: false,
    });
  }

  private subscribeToTimezone(): void {
    this.installationDetailsStore.installationTimezone$
      .pipe(
        filter(Boolean),
        map((val: string) => val),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((timeZone) => {
        this.installationLocalTimeOfErrorAppearedAt = this.datesService.getMomentFormattedDateForSpecificTimezone(
          this.activeError.appearedAt,
          timeZone
        );
      });
  }

  getDeviceNameFeature(
    deviceId: string
  ): Observable<Feature<DeviceNameProperties, DeviceNameCommands>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature<
      DeviceNameProperties,
      DeviceNameCommands
    >(FeatureNames.deviceName, deviceId);
  }

  getErrorCodeTranslation(errorCode: string, isDescription: boolean): string {
    let mappedErrorCode = errorCode.replace('.', '_');
    let translationKey = `TI_${mappedErrorCode}`;

    if (isDescription) {
      translationKey = translationKey + '_Desc';
    }

    const translation = this.translateService.instant(translationKey);

    const translationFound = translation !== translationKey;
    if (translationFound && isDescription) {
      return translation;
    } else {
      return this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.ERROR_LIST.ACTIVE_ERROR_INFORMATION.MISSING_MESSAGE'
      );
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
